import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useDarkMode } from "../../utils/useDarkMode";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RevenueChartProps {
  data: Array<{ month: string; revenue: number }>;
  height?: number;
  className?: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}

// ── Custom tooltip ────────────────────────────────────────────────────────────

function RevenueTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value ?? 0;

  return (
    <div className="rounded-lg border border-border-default bg-surface shadow-dropdown px-3 py-2">
      <p className="text-xs text-content-tertiary mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-content-primary">
        ₹{value.toLocaleString("en-IN")}
      </p>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function RevenueChart({ data, height = 260, className }: RevenueChartProps) {
  const isDark = useDarkMode();
  const brandColor = isDark ? "#7C3AED" : "#5B21B6";

  return (
    <div className={className} style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={brandColor} stopOpacity={isDark ? 0.30 : 0.25} />
              <stop offset="95%" stopColor={brandColor} stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-default)" vertical={false} />

          <XAxis
            dataKey="month"
            tick={{ fontSize: 11, fill: "var(--color-content-tertiary)" }}
            axisLine={false}
            tickLine={false}
            dy={6}
          />

          <YAxis
            tick={{ fontSize: 11, fill: "var(--color-content-tertiary)" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) =>
              v >= 100000
                ? `₹${(v / 100000).toFixed(1)}L`
                : v >= 1000
                ? `₹${(v / 1000).toFixed(0)}K`
                : `₹${v}`
            }
            width={52}
          />

          <Tooltip content={<RevenueTooltip />} cursor={{ stroke: brandColor, strokeWidth: 1, strokeDasharray: "4 2" }} />

          <Area
            type="monotone"
            dataKey="revenue"
            stroke={brandColor}
            strokeWidth={2.5}
            fill="url(#revenueGradient)"
            dot={false}
            activeDot={{ r: 5, fill: brandColor, stroke: isDark ? "#18181B" : "#fff", strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

RevenueChart.displayName = "RevenueChart";
